<template>
  <div class="voice-recorder">
    <audio ref="audioPlayer" @ended="handleAudioEnded" class="hidden"></audio>
    
    <div class="controls flex flex-col items-center gap-4">
      <div class="recording-status text-center" v-if="isRecording">
        <div class="text-red-500 animate-pulse">Recording...</div>
        <div class="recording-time">{{ formatTime(recordingTime) }}</div>
      </div>
      
      <div class="flex gap-4">
        <button 
          v-if="!isRecording" 
          @click="startRecording" 
          class="p-3 rounded-full hover:bg-gray-100 text-indigo-600 transition-colors"
          :disabled="!hasPermission"
          title="Start Recording"
        >
          <i class="fas fa-microphone text-4xl"></i>
        </button>
        <button 
          v-else 
          @click="stopRecording" 
          class="p-3 rounded-full hover:bg-red-50 text-red-600 transition-colors"
          :disabled="recordingTime < 5"
          :title="recordingTime < 5 ? 'Recording must be at least 5 seconds' : 'Stop Recording'"
        >
          <i class="far fa-stop-circle text-2xl"></i>
        </button>
      </div>

      <div v-if="audioUrl" class="preview-section w-full">
        <audio :src="audioUrl" controls class="w-full"></audio>
        <div class="flex justify-between mt-2">
          <input 
            v-model="recordingName" 
            type="text" 
            placeholder="Enter recording name" 
            class="form-input flex-grow mr-2"
          >
          <button 
            @click="saveRecording" 
            class="btn-indigo"
            :disabled="!recordingName"
          >
            Save Recording
          </button>
        </div>
      </div>

      <div v-if="!hasPermission" class="text-red-500">
        Please allow microphone access to record voicemails
      </div>
    </div>
  </div>
</template>

<script>
import { router } from '@inertiajs/vue3'

export default {
  props: {
    showManageRecordings: {
      type: Boolean,
      default: false
    },
    recordings: {
      type: Array,
      default: () => []
    }
  },

  emits: ['recordingSaved', 'recordingDeleted', 'recordingSelected'],

  data() {
    return {
      mediaRecorder: null,
      audioChunks: [],
      audioUrl: null,
      isRecording: false,
      hasPermission: false,
      recordingTime: 0,
      recordingName: '',
      timer: null,
      currentlyPlaying: null,
      currentlyPlayingId: null,
    }
  },
  mounted() {
    this.requestMicrophonePermission()
  },
  methods: {
    async requestMicrophonePermission() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.hasPermission = true
        stream.getTracks().forEach(track => track.stop())
      } catch (error) {
        console.error('Microphone permission denied:', error.message || error)
        this.hasPermission = false
      }
    },
    startRecording() {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          this.mediaRecorder = this.createMediaRecorder(stream);
          this.audioChunks = [];
          this.mediaRecorder.ondataavailable = this.handleDataAvailable;
          this.mediaRecorder.onstop = this.handleStopRecording;

          this.mediaRecorder.start(1000);
          this.isRecording = true;
          this.recordingTime = 0;
          this.timer = setInterval(() => {
            this.recordingTime++;
          }, 1000);
        })
        .catch(error => {
          console.error('Error starting recording:', error);
          this.hasPermission = false;
        });
    },
    createMediaRecorder(stream) {
      const mimeTypes = [
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/mp4'
      ];

      let selectedMimeType = null;
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      if (!selectedMimeType) {
        throw new Error('No supported audio MIME type found');
      }

      return new MediaRecorder(stream, {
        mimeType: selectedMimeType,
        audioBitsPerSecond: 128000
      });
    },
    handleDataAvailable(event) {
      if (event.data.size > 0) {
        this.audioChunks.push(event.data);
      }
    },
    handleStopRecording() {
      const audioBlob = new Blob(this.audioChunks, { type: this.mediaRecorder.mimeType });
      if (this.audioUrl) {
        URL.revokeObjectURL(this.audioUrl);
      }
      this.audioUrl = URL.createObjectURL(audioBlob);
    },
    stopRecording() {
      if (this.recordingTime < 5) {
        return
      }
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop()
        this.mediaRecorder.stream.getTracks().forEach(track => track.stop())
        this.isRecording = false
        clearInterval(this.timer)
      }
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    },
    async saveRecording() {
      try {
        const audioBlob = await fetch(this.audioUrl).then(r => r.blob())
        
        const timestamp = new Date().getTime()
        const filename = `${this.recordingName}_${timestamp}.wav`
        
        const formData = new FormData()
        formData.append('audio', audioBlob, filename)
        formData.append('name', this.recordingName)

        router.post('/voicemail/recording', formData, {
          forceFormData: true,
          onSuccess: () => {
            if (this.audioUrl) {
              URL.revokeObjectURL(this.audioUrl)
            }
            this.audioUrl = null
            this.recordingName = ''
            this.$emit('recordingSaved')
          },
          onError: (errors) => {
            console.error('Error saving recording:', errors)
          }
        })
      } catch (error) {
        console.error('Error processing audio:', error)
      }
    },
    togglePlay(recording) {
      const audioPlayer = this.$refs.audioPlayer;
      const isCurrentlyPlaying = this.isPlaying(recording);
      
      if (isCurrentlyPlaying) {
        audioPlayer.pause();
        audioPlayer.currentTime = 0;
        this.currentlyPlaying = null;
        this.currentlyPlayingId = null;
        this.isRecording = false;
        this.$emit('playbackStateChanged', null);
      } else {
        if (this.currentlyPlaying) {
          audioPlayer.pause();
          audioPlayer.currentTime = 0;
        }

        audioPlayer.src = `/storage/${recording.file_path}`;
        this.currentlyPlaying = recording;
        this.currentlyPlayingId = recording.id;
        
        audioPlayer.play().catch(error => {
          console.error('Error playing audio:', error);
          this.currentlyPlaying = null;
          this.currentlyPlayingId = null;
          this.$emit('playbackStateChanged', null);
        });
        
        this.$emit('playbackStateChanged', recording);
      }
    },
    isPlaying(recording) {
      return this.currentlyPlaying && this.currentlyPlaying.id === recording.id;
    },
    handleAudioEnded() {
      this.currentlyPlaying = null;
      this.currentlyPlayingId = null;
      this.$emit('playbackStateChanged', null);
    },
    deleteRecording(id) {
      if (confirm('Are you sure you want to delete this recording?')) {
        router.delete(`/voicemail/recording/${id}`, {
          preserveState: true,
          preserveScroll: true,
          onSuccess: () => {
            this.$emit('recordingDeleted', id);
          },
          onError: (errors) => {
            console.error('Failed to delete recording:', errors)
          }
        });
      }
    },
    selectRecording(recording) {
      this.$emit('recordingSelected', recording);
    }
  },
  beforeUnmount() {
    const audioPlayer = this.$refs.audioPlayer;
    if (audioPlayer) {
      audioPlayer.pause();
      audioPlayer.currentTime = 0;
    }
    if (this.audioUrl) {
      URL.revokeObjectURL(this.audioUrl);
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
  }
}
</script>

<style scoped>
.voice-recorder button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.recordings-list {
  max-height: 200px;
  overflow-y: auto;
}

.recordings-list::-webkit-scrollbar {
  width: 6px;
}

.recordings-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.recordings-list::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.recordings-list::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style> 